import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { UserPlus, Trash2, ShieldCheck, KeyRound } from "lucide-react";
import { OrgManager } from "@/components/OrgManager";
import { WaitlistQueue, AnalyticsPanel, PlansPanel, DeletionRequestsPanel, EmailLogPanel } from "@/components/AdminPanels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = ["public", "pro", "intelligence", "enterprise", "admin"];
const ADDONS = [["tools", "Tools"], ["network", "Network"]];
const STATUSES = ["invited", "active", "trial", "past_due", "suspended", "cancelled"];

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "user", tier: "pro" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/users`);
      setUsers(r.data || []);
    } catch {
      toast.error("Failed to load users");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/invite`, {
        email: form.email.trim(),
        name: form.name.trim(),
        role: form.role,
        subscription_tier: form.tier,
      });
      if (r.data?.invite_email_sent) toast.success(`Invited ${form.email.trim()}, a set-your-password link was emailed`);
      else toast.warning(`Invite email could not be sent. Forward this one-time setup link: ${r.data?.setup_link}`, { duration: 30000 });
      setForm({ email: "", name: "", role: "user", tier: "pro" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invite failed");
    } finally {
      setSubmitting(false);
    }
  };

  const requireReset = async (u) => {
    if (!window.confirm(`Require a password reset for ${u.email}? They will be signed out everywhere and emailed a temporary password.`)) return;
    try {
      const r = await axios.post(`${API}/auth/users/${u.user_id}/require-reset`, { reason: "suspected_compromise" });
      if (r.data?.email_sent) toast.success(`Reset required for ${u.email}, temporary password emailed`);
      else toast.warning(`Email failed. Temporary password for ${u.email}: ${r.data?.temporary_password}`, { duration: 30000 });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    }
  };

  const requireResetAll = async () => {
    if (!window.confirm("Require a password reset for EVERY user (except you)? All of them will be signed out and emailed temporary passwords. Use this only for a confirmed compromise.")) return;
    try {
      const r = await axios.post(`${API}/auth/users/require-reset-all`, { reason: "confirmed_compromise" });
      toast.success(`Reset required for ${r.data?.reset} users (${r.data?.emails_sent} emails sent)`);
      if (r.data?.undelivered?.length) toast.warning(`${r.data.undelivered.length} email(s) failed, see the email log`, { duration: 15000 });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk reset failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will lose all access.`)) return;
    try {
      await axios.delete(`${API}/auth/users/${u.user_id}`);
      toast.success(`Removed ${u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const patchUser = async (u, changes, label) => {
    try {
      await axios.patch(`${API}/auth/users/${u.user_id}`, changes);
      toast.success(`${u.email}: ${label}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
        <Navigation />
        <p className="text-center text-white/55 py-24">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
      <Navigation />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <h1 className="text-2xl font-black text-white flex items-center gap-2.5">
            <ShieldCheck className="w-6 h-6 text-orange-400" /> User Access
          </h1>
          <button type="button" onClick={requireResetAll}
            className="flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-bold text-amber-300 hover:bg-amber-500/20 transition-colors"
            data-testid="require-reset-all-button" title="Confirmed compromise: force every user onto a new temporary password">
            <KeyRound className="w-3.5 h-3.5" /> Require reset for all users
          </button>
        </div>

        {/* Invite form */}
        <form onSubmit={invite} className="whip-surface rounded-xl border border-white/10 p-5 mb-8" data-testid="admin-invite-form">
          <p className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 mb-4 flex items-center gap-2">
            <UserPlus className="w-3.5 h-3.5 text-orange-400" /> Invite a user
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <input required type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder-white/25 outline-none focus:border-orange-500/60"
              data-testid="invite-email-input" />
            <input type="text" placeholder="Name (optional)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder-white/25 outline-none focus:border-orange-500/60"
              data-testid="invite-name-input" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-orange-500/60"
              data-testid="invite-role-select">
              <option value="user" className="bg-[#12121a]">User</option>
              <option value="admin" className="bg-[#12121a]">Admin</option>
            </select>
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}
              className="rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-orange-500/60"
              data-testid="invite-tier-select">
              {TIERS.map((t) => (
                <option key={t} value={t} className="bg-[#12121a]">{t.charAt(0).toUpperCase() + t.slice(1)} tier</option>
              ))}
            </select>
          </div>
          <p className="text-[11px] text-white/35 mt-3">
            WHIP emails the invitee a single-use link (valid 48 hours) to set their own password. You never see it. Google sign-in also works with this exact email.
          </p>
          <button type="submit" disabled={submitting}
            className="mt-4 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-2 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400 transition-colors disabled:opacity-60"
            data-testid="invite-submit-button">
            {submitting ? "Inviting…" : "Send invite"}
          </button>
        </form>

        {/* Users list */}
        <div className="whip-surface rounded-xl border border-white/10 overflow-hidden" data-testid="admin-users-list">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
            <span className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60">Members</span>
            <span className="text-[11px] font-mono text-white/35">{users.length} ACCOUNTS</span>
          </div>
          {users.map((u, i) => (
            <div key={u.user_id}>
              <div className="px-5 py-3.5 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm flex items-center gap-2">
                    {u.name || u.email}
                    {u.founding_user && (
                      <span className="whip-pill !py-0.5 !px-2 !text-[9px] !text-amber-300 !border-amber-500/40 !bg-amber-500/10" data-testid={`founding-badge-${u.email}`}>
                        FOUNDING
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-white/45 font-mono">{u.email}</p>
                </div>
                <select
                  value={u.subscription_tier || "public"}
                  onChange={(e) => patchUser(u, { subscription_tier: e.target.value }, `tier → ${e.target.value}`)}
                  className="rounded-md border border-white/15 bg-white/[0.03] px-2 py-1 text-[11px] font-mono uppercase text-orange-300 outline-none focus:border-orange-500/60"
                  data-testid={`tier-select-${u.email}`}
                >
                  {TIERS.map((t) => (
                    <option key={t} value={t} className="bg-[#12121a] text-white">{t.toUpperCase()}</option>
                  ))}
                </select>
                <div className="flex items-center gap-1" data-testid={`addons-${u.email}`}>
                  {ADDONS.map(([key, label]) => {
                    const on = (u.addons || []).includes(key);
                    return (
                      <button key={key} type="button" title={`${label} add-on`}
                        onClick={() => patchUser(u, { addons: on ? (u.addons || []).filter((a) => a !== key) : [...(u.addons || []), key] }, `${label} add-on ${on ? "off" : "on"}`)}
                        className={`rounded-md border px-2 py-1 text-[10px] font-mono uppercase transition-colors ${on ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-white/15 bg-white/[0.03] text-white/40 hover:text-white/70"}`}
                        data-testid={`addon-${key}-${u.email}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <select
                  value={u.subscription_status || "active"}
                  onChange={(e) => patchUser(u, { subscription_status: e.target.value }, `status → ${e.target.value}`)}
                  className="rounded-md border border-white/15 bg-white/[0.03] px-2 py-1 text-[11px] font-mono uppercase text-white/70 outline-none focus:border-orange-500/60"
                  data-testid={`status-select-${u.email}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s} className="bg-[#12121a] text-white">{s.toUpperCase()}</option>
                  ))}
                </select>
                <button
                  onClick={() => patchUser(u, { founding_user: !u.founding_user }, u.founding_user ? "founding removed" : "founding granted")}
                  className={`whip-pill !py-0.5 !px-2 !text-[9px] transition-colors ${u.founding_user ? "!text-amber-300 !border-amber-500/40 !bg-amber-500/10" : "!text-white/35 hover:!text-amber-300"}`}
                  title={u.founding_user ? "Remove Founding Member access" : "Grant Founding Member access (Intelligence-level)"}
                  data-testid={`founding-toggle-${u.email}`}
                >
                  ★ FOUNDING
                </button>
                <span className={`whip-pill !py-0.5 !px-2 !text-[9px] ${u.role === "admin" ? "!text-orange-300 !border-orange-500/40 !bg-orange-500/10" : ""}`}>
                  {u.role.toUpperCase()}
                </span>
                {u.must_change_password && (
                  <span className="whip-pill !py-0.5 !px-2 !text-[9px] !text-amber-300 !border-amber-500/40 !bg-amber-500/10" title="Temporary password issued, change pending" data-testid={`pending-reset-${u.email}`}>
                    RESET PENDING
                  </span>
                )}
                {u.hasPassword && (
                  <button onClick={() => requireReset(u)} className="text-white/35 hover:text-amber-300 transition-colors"
                    data-testid={`require-reset-${u.email}`} title="Require password reset (suspected compromise)">
                    <KeyRound className="w-4 h-4" />
                  </button>
                )}
                {u.user_id !== user?.user_id && (
                  <button onClick={() => remove(u)} className="text-white/35 hover:text-red-400 transition-colors"
                    data-testid={`delete-user-${u.email}`} title="Remove user">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {i < users.length - 1 && <div className="whip-divider" />}
            </div>
          ))}
        </div>

        <OrgManager />
        <WaitlistQueue />
        <EmailLogPanel />
        <DeletionRequestsPanel />
        <PlansPanel />
        <AnalyticsPanel />
      </main>
    </div>
  );
}
